import { Check, ExternalLink } from "lucide-react";

const OPTIMAGROWTH_URL = "https://www.optimagrowth.agency";

/**
 * Reusable, clickable OptimaGrowth partnership panel.
 * Used across all industry landing pages to surface the SEO partnership.
 *
 * - The entire card is a hyperlink to www.optimagrowth.agency (new tab).
 * - SEO services are billed separately by OptimaGrowth (unique USP, not a bundle).
 *
 * Props:
 *   title:  string  - card heading (e.g. "Medical SEO partner")
 *   body:   string  - 1-2 sentence value prop
 *   bullets: string[] - up to 3 quick-win bullets
 *   variant: "default" | "compact"  (compact = smaller padding for hero panels)
 */
export const OptimaGrowthPanel = ({ title, body, bullets = [], variant = "default", testId = "optimagrowth-panel" }) => {
  const pad = variant === "compact" ? "p-6 sm:p-7" : "p-6 sm:p-8";
  return (
    <a
      href={OPTIMAGROWTH_URL}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={testId}
      className={`group block rounded-2xl border border-border bg-card/80 backdrop-blur-sm ${pad} shadow-[0_30px_80px_rgba(0,0,0,0.4)] hover:border-[hsl(var(--primary))/0.55] transition-colors`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <img
          src="/optimagrowth-on-dark.png"
          alt="OptimaGrowth - growth partner"
          className="h-7 w-auto object-contain"
        />
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
          Visit site <ExternalLink className="h-3 w-3" />
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-2">Billed separately · Unique partnership</div>
      <h3 className="font-display uppercase text-foreground text-xl tracking-[0.02em] mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      {bullets.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm text-foreground/85">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-foreground/70 group-hover:text-[hsl(var(--primary))] transition-colors">
        optimagrowth.agency &rarr;
      </p>
    </a>
  );
};
